import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { Observable } from "rxjs";
import { UserService } from "../../core/user/user.service";
import { User } from "../../core/user/user";
import { MenuService } from "../../core/menu/menu.service";
import { PoMenuItem } from "@po-ui/ng-components";

interface QuickAccessCard {
  badge: string;
  title: string;
  description: string;
  link: string;
  icon: string;
}

@Component({
  selector: 'erpcloud-dash',
  standalone: false,
  templateUrl: './dash.component.html',
  styleUrls: ['./dash.component.css']
})
export class DashComponent {
  user$: Observable<User | null>;
  userInfo?: User | null = null;
  userName: string = "";
  currentDate: string = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  private menuSchema: PoMenuItem[] = [];

  private readonly quickAccessBase: QuickAccessCard[] = [
    {
      badge: 'Cadastro',
      title: 'Empresas',
      description: 'Cadastre, consulte e gerencie todas as empresas do escritório.',
      link: '/empresa',
      icon: 'an an-building'
    },
    {
      badge: 'Relatório',
      title: 'Razão Contábil',
      description: 'Gere o relatório de razão contábil com base nos dados do sistema Domínio.',
      link: '/razao-contabil',
      icon: 'an an-chart-line-up'
    },
    {
      badge: 'Segurança',
      title: 'Controle de Acesso',
      description: 'Gerencie permissões, usuários e políticas de acesso ao sistema.',
      link: '/access',
      icon: 'an an-shield-check'
    },
    {
      badge: 'Domínio',
      title: 'Relatórios',
      description: 'Processe e baixe relatórios Tomados, Prestados e Desoneração.',
      link: '/reports',
      icon: 'an an-file-text'
    },
    {
      badge: 'Ferramentas',
      title: 'Automações',
      description: 'Correção de CFOPs, normalização SPED e outras ferramentas contábeis.',
      link: '/contabil-outros',
      icon: 'an an-wrench'
    },
    {
      badge: 'Fiscal',
      title: 'Pendências',
      description: 'Consulte pendências fiscais junto à Receita Federal por empresa.',
      link: '/pendencias',
      icon: 'an an-warning'
    }
  ];

  constructor(
    private readonly userService: UserService,
    private readonly menuService: MenuService,
    private router: Router
  ) {
    this.user$ = this.userService.getUser();

    this.user$.subscribe(x => {
      this.userInfo = x,
        this.userName = x?.name as string
    });

    const cachedMenu = localStorage.getItem('menuSchema');
    if (cachedMenu) {
      try {
        this.menuSchema = JSON.parse(cachedMenu);
      } catch {
        this.menuSchema = [];
      }
    }

    this.menuService.getMenuSchema().subscribe({
      next: (menus) => {
        this.menuSchema = menus || [];
      },
      error: () => {
        // Mantém fallback no cache quando falhar
      }
    });
  }

  redirect(url: string) {
    console.log("Redirecting to: ", url);
    this.router.navigate([url]);
  }

  get quickAccessCards(): QuickAccessCard[] {
    const linksPermitidos = this.getAllowedLinks(this.menuSchema);
    const flatMenus = this.flattenMenus(this.menuSchema).filter(m => !!m.link);

    // Cards padrão filtrados por permissão
    const filteredBase = this.quickAccessBase
      .filter(card => linksPermitidos.has(card.link))
      .map(card => {
        const menuItem = this.findMenuByLink(flatMenus, card.link);
        return {
          ...card,
          icon: this.getMenuIcon(menuItem) || card.icon
        };
      });
    const usedLinks = new Set(filteredBase.map(c => this.normalizeLink(c.link)));

    if (flatMenus.length === 0) return filteredBase;

    // Itens extras permitidos além dos padrões (ex.: Contas a Pagar / Omie)
    const extraMenus = flatMenus.filter(m => {
      const link = this.normalizeLink(String(m.link || ''));
      if (!link || usedLinks.has(link)) return false;
      if (!linksPermitidos.has(link)) return false;
      if (link === '/home') return false;
      return true;
    });

    const integracoesIcon = this.getIntegracoesIcon(this.menuSchema);
    const extras = extraMenus.map((m): QuickAccessCard => {
      const title = String(m.label || 'Menu');
      const isContasPagar = title.toLowerCase().includes('contas a pagar');
      const parentLabel = String((m as any).__parentLabel || '').trim();
      return {
        badge: parentLabel || 'Acesso',
        title,
        description: 'Acesso rápido ao menu liberado para seu perfil.',
        link: this.normalizeLink(String(m.link || '/home')),
        icon: isContasPagar ? integracoesIcon : (this.getMenuIcon(m) || 'an an-squares-four')
      };
    });

    // Se existir "omie", prioriza extras Omie antes dos demais
    const extrasOmie = extras.filter(c =>
      c.title.toLowerCase().includes('omie') || c.link.toLowerCase().includes('omie')
    );
    const extrasNaoOmie = extras.filter(c => !extrasOmie.includes(c));

    return this.pickCardsByMenuDiversity([...filteredBase, ...extrasOmie, ...extrasNaoOmie], 6);
  }

  private getAllowedLinks(menus: PoMenuItem[]): Set<string> {
    const links = new Set<string>();
    for (const item of this.flattenMenus(menus)) {
      const link = String(item.link || '').trim();
      if (link) links.add(link.startsWith('/') ? link : `/${link}`);
    }
    return links;
  }

  private flattenMenus(menus: PoMenuItem[]): PoMenuItem[] {
    const out: PoMenuItem[] = [];
    const walk = (items: PoMenuItem[], parentIcon?: string, parentLabel?: string) => {
      for (const it of items || []) {
        const currentIcon = String((it as any).icon || parentIcon || '').trim();
        const currentLabel = String(it.label || '').trim();
        const itemWithInheritedIcon: PoMenuItem = {
          ...it,
          icon: currentIcon,
          __parentLabel: parentLabel || currentLabel
        } as PoMenuItem;

        out.push(itemWithInheritedIcon);

        if (it.subItems?.length) {
          walk(it.subItems as PoMenuItem[], currentIcon, currentLabel || parentLabel);
        }
      }
    };
    walk(menus || []);
    return out;
  }

  private findMenuByLink(menus: PoMenuItem[], link: string): PoMenuItem | undefined {
    const target = this.normalizeLink(link);
    return menus.find(m => this.normalizeLink(String(m.link || '')) === target);
  }

  private normalizeLink(link: string): string {
    if (!link) return '';
    return link.startsWith('/') ? link : `/${link}`;
  }

  private getMenuIcon(menuItem?: PoMenuItem): string {
    if (!menuItem) return '';
    return String((menuItem as any).icon || '').trim();
  }

  private getIntegracoesIcon(menus: PoMenuItem[]): string {
    const flat = this.flattenMenus(menus || []);
    const integracoes = flat.find(m => String(m.label || '').toLowerCase().includes('integra'));
    return this.getMenuIcon(integracoes) || 'an an-link-simple';
  }

  private pickCardsByMenuDiversity(cards: QuickAccessCard[], maxCards: number): QuickAccessCard[] {
    if (cards.length <= maxCards) return cards;

    // 1) Pega 1 card por menu/categoria (badge) para representar diversidade.
    const byBadge: QuickAccessCard[] = [];
    const usedBadges = new Set<string>();
    for (const card of cards) {
      const badge = (card.badge || 'Acesso').toLowerCase();
      if (!usedBadges.has(badge)) {
        byBadge.push(card);
        usedBadges.add(badge);
      }
      if (byBadge.length === maxCards) return byBadge;
    }

    // 2) Completa até o limite com os demais cards, mantendo ordem original.
    const selectedLinks = new Set(byBadge.map(c => this.normalizeLink(c.link)));
    for (const card of cards) {
      const link = this.normalizeLink(card.link);
      if (!selectedLinks.has(link)) {
        byBadge.push(card);
        selectedLinks.add(link);
      }
      if (byBadge.length === maxCards) break;
    }

    return byBadge;
  }
}
